export const ACCOUNT_STATE_STORAGE_KEY = "zesrin.account-state-v1";
export const ACCOUNT_STATE_TTL_MS = 14 * 24 * 60 * 60 * 1000;

/** Last resolved account state, used to paint the header before a session bootstrap finishes. */
export type AccountStateHint = "authenticated" | "guest";

/**
 * Reads the stored account state hint. Only the resolved state and its timestamp are kept:
 * access tokens and profile data stay in memory and are never persisted here. Anything
 * unreadable, expired, or from the future is ignored so the caller can fall back to a
 * neutral placeholder instead of guessing an identity.
 */
export function readAccountStateHint(now: number = Date.now()): AccountStateHint | undefined {
  try {
    const stored = window.localStorage.getItem(ACCOUNT_STATE_STORAGE_KEY);
    if (!stored) return undefined;
    const parsed = JSON.parse(stored) as { state?: unknown; checkedAt?: unknown };
    if (parsed.state !== "authenticated" && parsed.state !== "guest") return undefined;
    if (typeof parsed.checkedAt !== "number" || !Number.isFinite(parsed.checkedAt)) return undefined;
    const age = now - parsed.checkedAt;
    if (age < 0 || age > ACCOUNT_STATE_TTL_MS) return undefined;
    return parsed.state;
  } catch {
    // Storage can be unavailable or hold foreign data; the header keeps its neutral placeholder.
    return undefined;
  }
}

export function rememberAccountState(state: AccountStateHint, now: number = Date.now()): void {
  try {
    window.localStorage.setItem(ACCOUNT_STATE_STORAGE_KEY, JSON.stringify({ state, checkedAt: now }));
  } catch {
    // Persisting the hint is optional: the next load simply starts from the neutral placeholder.
  }
}
